import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Overlay, CreateOverlay, UpdateOverlay } from '@seencast/shared';
import { request, type ListEnvelope } from './client.ts';
import { qk } from './keys.ts';

// Overlays (docs/09 §C2): CRUD over /v1/overlays. DELETE → 409 when the overlay is
// referenced (a screen or the org default).

export type { Overlay, CreateOverlay, UpdateOverlay };

export function listOverlays(): Promise<Overlay[]> {
  return request<ListEnvelope<Overlay>>('/overlays').then((r) => r.items);
}

export function useOverlays() {
  return useQuery({ queryKey: qk.overlays, queryFn: listOverlays });
}

export function useCreateOverlay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOverlay) =>
      request<Overlay>('/overlays', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.overlays }),
  });
}

export function useUpdateOverlay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOverlay }) =>
      request<Overlay>(`/overlays/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.overlays }),
  });
}

export function useDeleteOverlay() {
  const qc = useQueryClient();
  return useMutation({
    // Throws ApiError(409, 'referenced') when a screen/default still uses it.
    mutationFn: (id: string) => request<void>(`/overlays/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.overlays }),
  });
}
